import React, { useEffect, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import { GLTFResult } from '../../types/GLTFResults'
import * as THREE from 'three'
import { UnsignedByteType } from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'
import hdr from './studio.hdr'
import { PMREMGenerator } from 'three/src/extras/PMREMGenerator'

type HospitalRoomModelProps = JSX.IntrinsicElements['group'] &
  {
    cameraTrackCallback?: (node: THREE.Object3D) => void
    animationState: number,
    startedMoveAnimationCallback?: (startingAnimationNumber: number) => void,
    finishedMoveAnimationCallback?: (finishingAnimationNumber: number) => void,
    canPlay: boolean,
    loadedCallback?: () => void
  }

const framerate = 25

type AnimationState = {
  animationClipNumber: number
  startFrame: number
  endFrame: number
}

/*
0 - 66 - Anim1,
67 - 72 - Stopped,
73 - 163 - Anim2
*/

function getAnimationStateTime (_state: number): AnimationState {
  switch (_state) {
    case 0 :
      return { animationClipNumber: 0, startFrame: 0, endFrame: 66 }
    case 1 :
      return { animationClipNumber: 1, startFrame: 73, endFrame: 162 }
    default :
      return { animationClipNumber: 0, startFrame: 0, endFrame: 0 }
  }
}

const HospitalKitchenModel = React.forwardRef<any, HospitalRoomModelProps>((props, ref) => {
  const {
    scene,
    animations
  }: { scene: THREE.Scene, nodes: any, materials: any, animations: THREE.AnimationClip[] } = useGLTF('gltfs/HospitalKitchen/Kitchen_01.gltf') as unknown as GLTFResult
  const [mixer] = useState<THREE.AnimationMixer>(new THREE.AnimationMixer(scene))

  const [currentAnimationFrames, setCurrentAnimationFrames] = useState<AnimationState>(getAnimationStateTime(0))

  useEffect(() => {
    const clip = mixer?.clipAction(animations[0])
    clip.play()
    clip.paused = true

    scene.traverse((node: THREE.Object3D) => {
      if (node.name === 'CAM') {
        if (props.cameraTrackCallback != null) {
          props.cameraTrackCallback(node)
        }
      }
    })

    if (props.loadedCallback != null) props.loadedCallback()

    return () => {
    }
  }, [])

  useEffect(() => {
    const newFrameRange = getAnimationStateTime(props.animationState)
    setCurrentAnimationFrames(newFrameRange)

    const action = mixer?.clipAction(animations[0])
    if (props.canPlay) {
      action.play()
      action.paused = false
      if (props.startedMoveAnimationCallback != null) {
        props.startedMoveAnimationCallback(props.animationState)
        document.dispatchEvent(new CustomEvent('RoomModelAnimationPlay'))
      }
    }
    mixer?.setTime(newFrameRange.startFrame / framerate)
  }, [props.animationState, props.canPlay])

  useFrame((_state, _deltaTime) => {
    if (mixer !== null) {
      const action = mixer.clipAction(animations[0])
      if (!action.paused) {
        if (mixer?.time > (currentAnimationFrames.endFrame / framerate)) {
          action.paused = true
          if (props.finishedMoveAnimationCallback != null) props.finishedMoveAnimationCallback(currentAnimationFrames.animationClipNumber)
        } else {
          mixer?.update(_deltaTime)
        }
      }
    }
  })

  // Initialise HDR Lighting
  const { gl } = useThree()
  const pmRemGenerator = new PMREMGenerator(gl)
  const loader = new RGBELoader()
  loader.setDataType(UnsignedByteType)
  pmRemGenerator.compileEquirectangularShader()

  useEffect(() => {
    loader.load(hdr, (texture) => {
      scene.environment = pmRemGenerator.fromEquirectangular(texture).texture
      texture.dispose()
      pmRemGenerator.dispose()
    })
  }, [scene, loader, pmRemGenerator])

  return (
    <group ref={ref}>
      <primitive {...props} object={scene} />
    </group>
  )
})

HospitalKitchenModel.displayName = 'HospitalKitchenModel'

export default HospitalKitchenModel
